<script>
  import { createEventDispatcher } from "svelte";
  import upperCase from "lodash/upperCase";
  import { navigate } from "svelte-routing";
  import { tooltip } from "svelte-utilities";

  import ReviewerMoreButton from "#lib/home/ReviewerMoreButton.svelte";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import ShareIcon from "@local/assets/icons/share.svg";
  import ProfileIcon from "@local/assets/icons/profile.svg";
  import DocumentThumbnail from "#lib/DocumentThumbnailMini.svelte";
  import StatusButton from "#lib/StatusButton.svelte";
  import ArrowRightIcon from "@local/assets/icons/arrow-right.svg";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";

  import formatHumanTime from "@local/extensions/formatters/format-human-time.js";
  import getUserInitials from "@local/extensions/formatters/get-user-initials.js";
  import { profile } from "#src/stores/auth.js";

  export let job;
  export let deleting = false;

  let expanded = false;

  $: groupBy = job?.group_data.settings.output_group_by || "type";
  $: showTotalUnits = job?.group_data.settings.output_show_total_units ?? true;
  $: showTotalCategories = job?.group_data.settings.output_show_total_categories ?? true;

  const dispatch = createEventDispatcher();

  function itemCount(count, label = "unit") {
    if (!count) return `0 ${label}s`;
    if (count === 0) return `0 ${label}s`;
    if (count === 1) return `1 ${label}`;
    return `${count} ${label}s`;
  }

  function nav(doc) {
    if (doc.type === "remote") {
      window.open(doc.data.url, "_blank");
    } else {
      navigate(`/jobs/${job.id}/documents/${doc.id}`);
    }
  }
</script>

<button
  class="card-container text-left relative border p-4 border-gray-300 bg-white rounded-lg shadow hover:shadow-lg flex justify-start"
  on:dblclick={() => navigate(`/jobs/${job.id}/summary`)}
  class:opacity-50={deleting}
  class:border-dashed={deleting}>
  <div class:invisible={deleting} class="text-xs space-y-4 w-full">
    <div class="flex justify-between items-center">
      <div class="flex gap-2">
        <div class="font-bold">
          {job.name}
        </div>
        <div class="text-gray-500 italic">
          {formatHumanTime(job.created_at)}
        </div>
        <div class="flex-none">
          {#if job.assigned_to_username}
            <div
              class="profile-circle relative"
              style="background-color: {job.assigned_to_user_color ||
                '#000'}; border-color: {job.assigned_to_user_color || '#000'}">
              {upperCase(
                getUserInitials({
                  username: job.assigned_to_username,
                  email: job.assigned_to_email,
                }),
              )}
            </div>
          {:else if !job.assigned_to}
            <div class="text-gray-400">
              <ProfileIcon />
            </div>
          {/if}
        </div>
      </div>
      <div class="flex gap-2">
        <button
          class="btn btn-compact flex gap-1"
          on:click={() => navigate(`/jobs/${job.id}/summary`)}
          use:tooltip={{ text: "View Job Summary" }}>
          <ShareIcon />
        </button>
        <button
          class="btn btn-compact flex gap-1"
          on:click={() => dispatch("download-job", { id: job.id })}
          use:tooltip={{ text: "Download" }}>
          <DownloadIcon />
        </button>
        <ReviewerMoreButton
          deletable
          jobid={job.id}
          on:archive-job={() => dispatch("archive-job", { id: job.id })}
          on:clone-job={() => dispatch("clone-job", { id: job.id })}
          on:delete-job={() => dispatch("delete-job", { id: job.id })}
          on:export-json={() => dispatch("export-json", { id: job.id })} />
      </div>
    </div>

    <div>
      {#if job.documents_count}
        {@const visibleDocs = expanded ? job.documents_array : job.documents_array.slice(0, 3)}
        <div class="flex gap-4 flex-wrap">
          {#each visibleDocs as doc}
            {#key doc.id}
              <button
                class="w-24 border rounded-lg bg-white p-2 shadow text-left hover:shadow-lg"
                on:click={() => nav(doc)}>
                <div class="relative w-full aspect-square border">
                  <DocumentThumbnail {doc} />
                </div>
                <div class="truncate text-xs mt-1">
                  {doc.name}
                </div>
              </button>
            {/key}
          {/each}
        </div>
      {:else}
        <div class="text-gray-500 italic">No documents</div>
      {/if}
    </div>
    {#if job.documents_count > 3}
      <button class="text-xs btn-text" on:click={() => (expanded = !expanded)}>
        {expanded ? "Show less" : "Show all"}
      </button>
    {/if}
  </div>
  {#if deleting}
    <div class="absolute top-0 left-0 flex gap-4 items-center p-4">
      <div class="text-gray-800 h-8 w-8 relative">
        <div class="absolute">
          <div class="animate-spin">
            <SpinnerIcon />
          </div>
        </div>
      </div>
      <div class="text-gray-800 italic text-sm">Deleting job...</div>
    </div>
  {/if}
</button>
